import {useCallback} from 'react';
import {getStore} from 'src/v2/state/store';
import {convertGraphQlToTransactionDetails} from 'src/v2/util/convertGraphQlToStoreState';
import {
  OpenapiCryptocurrencyPayinsResourceConsumerWalletSessionProviderEnum,
  OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum,
  OpenapiPostV1CryptoInternalPayinSessionPrepareInputNetworkEnum,
} from '@stripe-internal/data-v1/schema/types';
import useConfirmCryptoPurchaseMutation from 'src/api/graphql/useConfirmCryptoPurchaseMutation';

interface ConfirmPaymentParams {
  selectedCurrency: OpenapiCryptocurrencyPayinsResourceSupportedCurrencyAssetCodeEnum;
  walletAddress: string;
  network: OpenapiPostV1CryptoInternalPayinSessionPrepareInputNetworkEnum;
  walletProvider: OpenapiCryptocurrencyPayinsResourceConsumerWalletSessionProviderEnum;
  walletKey?: string;
  signedTransactionData?: string;
  transactionHash?: string;
  solanaSourceTokenAccountAddress?: string;
}

export const useConfirmSessionForPayment = () => {
  const store = getStore();
  const [confirmCryptoPurchase] = useConfirmCryptoPurchaseMutation();
  const sessionId = store.useSelector((state) => state.id);
  const clientSecret = store.useSelector((state) => state.clientSecret);

  return useCallback(
    async (params: ConfirmPaymentParams) => {
      const response = await confirmCryptoPurchase({
        variables: {
          input: {
            client_secret: clientSecret,
            crypto_payin_session: sessionId,
            network: params.network,
            currency: params.selectedCurrency,
            wallet_address: params.walletAddress,
            provider: params.walletProvider,
            wallet_key: params.walletKey,
            signed_transaction_data: params.signedTransactionData,
            transaction_hash: params.transactionHash,
            solana_source_token_account_address:
              params.solanaSourceTokenAccountAddress,
          },
        },
      });

      const updatedSession = response.data?.v1CryptoInternalPayinSessionConfirm;

      if (response.error) {
        throw response.error;
      }

      if (!updatedSession) {
        throw new Error('No session returned from confirmCryptoPurchase');
      }

      store.setValue((state) => {
        state.state = updatedSession.state;
        state.transactionDetails = convertGraphQlToTransactionDetails(
          updatedSession.transaction_details,
        );
      });
    },
    [sessionId, clientSecret, confirmCryptoPurchase, store],
  );
};
